import { computed, ref } from '@vue/composition-api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { MandatoryItem, IMandatoryItemData } from '@/models';
import { MaybeRef } from '@tanstack/vue-query/build/lib/types';
import providerInstance from '@/services/provider';

export function useFetchTariffMandatoryItems() {
  const queryKey = ref(['tariffMandatoryItems']);

  const { isLoading, isError, error, data: tariffMandatoryItems } = useQuery({
    queryKey: queryKey,
    queryFn: () =>
      providerInstance.tariffMandatoryItem.fetchTariffMandatoryItems(),
    enabled: true,
    select: data => {
      return data.map(
        tariffMandatoryItem => new MandatoryItem(tariffMandatoryItem)
      );
    }
  });

  return { queryKey, tariffMandatoryItems, error, isError, isLoading };
}

export function useFetchTariffMandatoryItem(id: MaybeRef<number>) {
  const tariffMandatoryItemIdRef = ref(id);

  const queryKey = ref(['tariffMandatoryItems', tariffMandatoryItemIdRef]);

  const { data: tariffMandatoryItem, isLoading, error } = useQuery({
    queryKey: queryKey,
    enabled: computed(() => Boolean(tariffMandatoryItemIdRef.value)),
    queryFn: () =>
      providerInstance.tariffMandatoryItem.fetchTariffMandatoryItem(
        tariffMandatoryItemIdRef.value
      ),
    select: data => new MandatoryItem(data)
  });

  return { tariffMandatoryItem, isLoading, error, queryKey };
}

export function useCreateTariffMandatoryItem() {
  const queryClient = useQueryClient();

  const {
    isLoading,
    isError,
    error,
    isSuccess,
    mutate,
    mutateAsync,
    reset
  } = useMutation({
    mutationKey: ['create-tariffMandatoryItem'],
    mutationFn: (tariffMandatoryItem: IMandatoryItemData) =>
      providerInstance.tariffMandatoryItem.createTariffMandatoryItem(
        tariffMandatoryItem
      ),

    onSuccess: data => {
      queryClient.setQueryData(['tariffMandatoryItems', data.id], data);
      queryClient.invalidateQueries({
        queryKey: ref(['tariffMandatoryItems'])
      });
    }
  });
  return {
    isLoading,
    isError,
    error,
    isSuccess,
    mutate,
    mutateAsync,
    reset
  };
}

export function useUpdateTariffMandatoryItem() {
  const queryClient = useQueryClient();

  const { isLoading, isError, error, isSuccess, mutate, reset } = useMutation({
    mutationKey: ['update-tariffMandatoryItem'],
    mutationFn: (tariffMandatoryItem: IMandatoryItemData) =>
      providerInstance.tariffMandatoryItem.updateTariffMandatoryItem(
        tariffMandatoryItem
      ),

    onSuccess: data => {
      queryClient.setQueryData(['tariffMandatoryItems', data.id], data);
      queryClient.invalidateQueries({
        queryKey: ref(['tariffMandatoryItems', data.id])
      });
      queryClient.invalidateQueries({
        queryKey: ref(['tariffMandatoryItems'])
      });
    }
  });
  return {
    isLoading,
    isError,
    error,
    isSuccess,
    mutate,
    reset
  };
}

export function useDeleteTariffMandatoryItem() {
  const mutationKey = ref(['delete-tariffMandatoryItem']);

  const queryClient = useQueryClient();

  const { isLoading, isError, error, isSuccess, mutate, reset } = useMutation({
    mutationKey: mutationKey.value,
    mutationFn: (id: number) =>
      providerInstance.tariffMandatoryItem.deleteTariffMandatoryItem(id),

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ref(['tariffMandatoryItems'])
      });
    }
  });
  return {
    isLoading,
    isError,
    error,
    isSuccess,
    mutate,
    reset
  };
}
